import { useEffect, useState, useMemo } from 'react';
import { postAppointment } from '../http/clubsApi';
import { deleteClasses } from '../http/classesApi';
import { useGetAppointmentsQuery } from '@/queries/useGetAppointmentsQuery';
import { useGetClientQuery } from '@/queries/useGetClientQuery';
import { useGetAppointmentServicesQuery } from '@/queries/useGetAppointmentServicesQuery';
import { getCookie } from '@/utils/cookies';

export const useSignUpSlot = (slot: any, trainers: any[], onSuccessfulSignUp: () => void) => {
  const [open, setOpen] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<string | null>(null);
  const [errorDialog, setErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [cancelSuccessDialog, setCancelSuccessDialog] = useState(false);
  const [isTimeBooked, setIsTimeBooked] = useState(false);
  const userToken: string | null = getCookie('user_token');
  const { data: appointmentsData, refetch: refetchAppointments } = useGetAppointmentsQuery();
  const { data: clientData } = useGetClientQuery();
  const { data: servicesData } = useGetAppointmentServicesQuery();

  const bookingIsAvailable = (slot) => {
    // disable booking for parachute 2 room
    // if (slot.room_id === 'c9fb4cce-dca1-11ef-9d3f-2a618cb265e1') return false;

    const currentTime = new Date();
    const exerciseTime = new Date(slot.date_time);
    const tomorrow = new Date();
    tomorrow.setDate(currentTime.getDate() + 1);

    // allow booking after tomorrow
    if (exerciseTime > tomorrow) return true;

    // disable booking after 21 for next day morning
    if (currentTime.getHours() > 21 && exerciseTime.getHours() < 10) return false;

    // disable booking one hour before exercise
    if (exerciseTime < new Date(currentTime.getTime() + 60 * 60 * 1000)) return false;

    // disable booking in the morning of today
    if (exerciseTime.toDateString() === currentTime.toDateString() && exerciseTime.getHours() < 10)
      return false;

    return true;
  };

  // Check both room-based restrictions and time-based availability
  const isBookingAvailable = bookingIsAvailable(slot);

  const userName = useMemo(() => {
    if (clientData) return `${clientData.name} ${clientData.last_name}`;

    return '';
  }, [clientData]);
  const serviceId = useMemo(() => {
    if (servicesData) {
      return `${servicesData[0].id}`;
    }
    return '';
  }, [servicesData]);
  useEffect(() => {
    if (appointmentsData) {
      setIsTimeBooked(
        appointmentsData.some((appointment) => appointment.start_date === slot.date_time),
      );
    }
  }, [appointmentsData, slot.date_time]);

  const handleSignUp = () => {
    const idClub = localStorage.getItem('id_club');
    postAppointment(slot.date_time, serviceId, slot.employee_id, slot.room_id, idClub, userToken)
      .then(() => {
        refetchAppointments();
        setOpen(false);
        setSuccessDialogOpen(true);
        setIsTimeBooked(true);
      })
      .catch((err) => {
        console.error(err);
        let message = 'Произошла ошибка при записи на занятие';
        if (err.response) {
          switch (err.response.data.error) {
            case 1034:
              message =
                'Вы расписали все доступные занятия, либо абонемент заканчивается раньше указанной даты.';
              break;
            default:
              message = err.response.data.error_message;
          }
        }
        setErrorMessage(message);
        setErrorDialog(true);
      });
  };

  const handleSuccessClose = () => {
    if (onSuccessfulSignUp) {
      onSuccessfulSignUp();
    }
    setSuccessDialogOpen(false);
  };

  const handleErrorClose = async () => {
    refetchAppointments();
    setErrorDialog(false);

    if (onSuccessfulSignUp) {
      onSuccessfulSignUp();
    }
  };

  useEffect(() => {
    refetchAppointments();
  }, [cancelSuccessDialog]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const options = {
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit',
    };
    return date.toLocaleDateString('ru-RU', options as Intl.DateTimeFormatOptions);
  };

  const handleCancelClass = () => {
    if (appointmentsData) {
      const appointment = appointmentsData.find((app) => app.start_date === slot.date_time);

      if (appointment) {
        setSelectedAppointment(appointment.appointment_id);
        setDeleteDialog(true);
      }
    }
  };

  const handleConfirmDelete = () => {
    deleteClasses(userToken, selectedAppointment)
      .then(() => {
        refetchAppointments();
        setDeleteDialog(false);
        setCancelSuccessDialog(true);
        setIsTimeBooked(false);
      })
      .catch((error) => {
        console.error('Error deleting appointment:', error);
      });
  };

  const [trainerListActiveState, setTrainerListActiveState] = useState([]);
  useEffect(() => {
    const trainerListActive = [];
    trainers.forEach((trainer) => {
      if (trainer.id === slot.employee_id) {
        trainerListActive.push(trainer);
      }
    });
    setTrainerListActiveState(trainerListActive);
    if (slot.appointment_id) {
      setIsTimeBooked(true);
    } else {
      setIsTimeBooked(false);
    }
  }, [slot]);

  const handleCancelSuccessClose = () => {
    setCancelSuccessDialog(false);
    if (onSuccessfulSignUp) {
      onSuccessfulSignUp();
    }
  };

  return {
    open,
    successDialogOpen,
    deleteDialog,
    selectedAppointment,
    errorDialog,
    errorMessage,
    cancelSuccessDialog,
    isTimeBooked,
    handleSignUp,
    handleSuccessClose,
    handleErrorClose,
    handleCancelClass,
    handleConfirmDelete,
    trainerListActiveState,
    handleCancelSuccessClose,
    formatDate,
    userName,
    setOpen,
    setDeleteDialog,
    setErrorDialog,
    setCancelSuccessDialog,
    isBookingAvailable,
  };
};
